import { Button } from "@aircall/ds";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { Section, SectionHeader, SectionTitle } from "@/blocks/Section/Section";
import { PercentBadge } from "@/components/PercentBadge/PercentBadge";
import { EvaluationFragment } from "@/graphql/generated/graphql";
import { useCall } from "@/hooks/useCall/useCall";
import { useCcAccess } from "@/hooks/useCcAccess/useCcAccess";
import { useEvaluation } from "@/hooks/useEvaluation/useEvaluation";
import { useEvaluationPercentage } from "@/hooks/useEvaluationPercentage/useEvaluationPercentage";
import { useBaseStore } from "@/store/store";

function NoEvaluation() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const setEvaluationTabSource = useBaseStore(
    (store) => store.setEvaluationTabSource
  );
  const setShouldTrackEvaluationTabSource = useBaseStore(
    (store) => store.setShouldTrackEvaluationTabSource
  );

  const { canCreate } = useCcAccess();

  const startCallEvaluationHandler = () => {
    setEvaluationTabSource("insights_cta");
    setShouldTrackEvaluationTabSource(true);
    navigate("../evaluation");
  };

  return (
    <div className="flex flex-col gap-2">
      <SectionHeader>
        <SectionTitle>{t("call_context.call_evaluation")}</SectionTitle>
      </SectionHeader>
      {canCreate ? (
        <div>
          <Button
            data-test="start-call-evaluation-button-AP"
            onClick={startCallEvaluationHandler}
            variant="link"
            size="lg"
            className="m-0 p-0"
          >
            {t("call_context.start_call_evaluation")}
          </Button>
        </div>
      ) : (
        <span className="text-xs font-normal text-muted-foreground">
          {t("call_context.no_call_evaluation")}
        </span>
      )}
    </div>
  );
}

type WithEvaluationProps = {
  evaluation: EvaluationFragment;
};

function WithEvaluation({ evaluation }: Readonly<WithEvaluationProps>) {
  const { t } = useTranslation();

  const { score, feedback } = evaluation;

  const { percentage } = useEvaluationPercentage({
    points: score.scorePoints,
    maximumPoints: score.totalPoints,
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <SectionHeader>
            <SectionTitle>{t("call_context.call_evaluation")}</SectionTitle>
          </SectionHeader>
          <span className="text-tiny font-normal">
            {t("evaluation.overall_score", {
              score: score.scorePoints,
              maxScore: score.totalPoints,
            })}
          </span>
        </div>
        <PercentBadge value={percentage} borderless />
      </div>
      <span className="text-sm font-normal">{feedback}</span>
    </div>
  );
}

function PickContent() {
  const { callId } = useParams();

  const { evaluation, loading } = useEvaluation(callId);

  if (loading) {
    return null;
  }

  if (!evaluation) {
    return <NoEvaluation />;
  }

  return <WithEvaluation evaluation={evaluation} />;
}

export function SectionCallEvaluation() {
  const { call } = useCall();

  if (!call) {
    return null;
  }

  return (
    <Section data-test="call-evaluation-section">
      <PickContent />
    </Section>
  );
}
